/**
 * Offer Modal Handler with Loading States
 * Handles the offer modal functionality including opening, closing, and submitting offers
 * Requires: OfferValidation library and window.offerData to be available
 */

const OfferModal = {
  
  /**
   * Initialize the offer modal functionality
   * Call this after the DOM is loaded
   */
  init() {
    this.bindEvents();
  },

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Event listener for opening modal
    const openModalBtn = document.getElementById('open-offer-modal');
    if (openModalBtn) {
      openModalBtn.addEventListener('click', () => {
        this.openModal();
      });
    }
  },

  /**
   * Open the offer modal
   */
  openModal() {
    const modal = document.getElementById('offer-modal');
    if (modal) {
      modal.style.display = 'block';
    }
  },

  /**
   * Close the offer modal and reset form
   */
  closeModal() {
    const modal = document.getElementById('offer-modal');
    const responseDiv = document.getElementById('offer-response');
    const offerAmount = document.getElementById('offerAmount');
    const offerEmail = document.getElementById('offerEmail');

    if (modal) modal.style.display = 'none';
    if (responseDiv) responseDiv.innerHTML = '';
    if (offerAmount) offerAmount.value = '';
    if (offerEmail) offerEmail.value = '';

    // Reset any loading states
    this.hideLoading();
  },

  /**
   * Show loading state
   */
  showLoading() {
    const submitBtn = document.getElementById('submit-offer-btn');
    const submitText = document.getElementById('submit-text');
    const spinner = document.getElementById('loading-spinner');
    const form = document.getElementById('offer-form');
    
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.style.background = '#999';
      submitBtn.style.cursor = 'not-allowed';
      submitBtn.style.transform = 'none';
    }
    
    if (submitText) submitText.textContent = 'Processing...';
    if (spinner) spinner.style.display = 'block';
    
    // Disable form inputs
    if (form) {
      const inputs = form.querySelectorAll('input, button');
      inputs.forEach(input => input.disabled = true);
    }
  },

  /**
   * Hide loading state
   */
  hideLoading() {
    const submitBtn = document.getElementById('submit-offer-btn');
    const submitText = document.getElementById('submit-text');
    const spinner = document.getElementById('loading-spinner');
    const form = document.getElementById('offer-form');
    
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.style.background = '#007cba';
      submitBtn.style.cursor = 'pointer';
    }
    
    if (submitText) submitText.textContent = 'Submit Offer';
    if (spinner) spinner.style.display = 'none';
    
    // Re-enable form inputs
    if (form) {
      const inputs = form.querySelectorAll('input, button');
      inputs.forEach(input => input.disabled = false);
    }
  },

  /**
   * Submit the offer form with loading states
   */
  async submitOffer() {
    const offerInput = document.getElementById('offerAmount')?.value || '';
    const emailInput = document.getElementById('offerEmail')?.value || '';
    const responseDiv = document.getElementById('offer-response');

    if (!responseDiv) {
      console.error("❌ Response div not found");
      return;
    }

    // Clear any previous responses
    responseDiv.innerHTML = '';

    // Check if OfferValidation library is available
    if (typeof OfferValidation === 'undefined') {
      console.error("❌ OfferValidation library not loaded");
      responseDiv.innerHTML = "⚠️ Validation system not available.";
      return;
    }

    // Validate all inputs using the validation library
    // Convert fullPrice from cents to dollars before validation
    // CORRECT - price is already in dollars
    const fullPriceInDollars = window.offerData ? Number(window.offerData.fullPrice) : null;
    const validation = OfferValidation.validateForm(offerInput, emailInput, fullPriceInDollars);
    
    if (!validation.isValid) {
      console.error("❌ Validation failed:", validation.errors);
      responseDiv.innerHTML = "⚠️ " + validation.errors[0];
      return;
    }

    // Check if window.offerData exists
    if (!window.offerData) {
      console.error("❌ Missing offerData.");
      responseDiv.innerHTML = "⚠️ Product data is missing.";
      return;
    }

    // Show loading state
    this.showLoading();

    try {
      // Set rate limit after validation passes
      OfferValidation.setRateLimit();

      // Use validated values
      const offer = validation.offer;
      const email = validation.email;

      // Use Number() for known numeric values to avoid NaN from strings
      const variantId = Number(window.offerData.variantId);
      const productId = Number(window.offerData.productId);
      const fullPrice = Number(window.offerData.fullPrice);
      const autoAccept = Number(window.offerData.autoAccept);
      const autoDecline = Number(window.offerData.autoDecline);

      console.log("📊 Offer Evaluation", {
        offer,
        autoAccept,
        autoDecline,
        fullPrice,
        accepted: offer >= autoAccept,
        declined: offer <= autoDecline
      });

      const amountOff = fullPrice - offer;

      if (offer >= autoAccept) {
        await this.handleAcceptedOffer(offer, email, amountOff, productId, variantId, responseDiv);
      } else if (offer <= autoDecline) {
        this.handleDeclinedOffer(offer, responseDiv);
      } else {
        this.handleUnexpectedState(responseDiv);
      }
    } catch (error) {
      console.error("❌ Unexpected error:", error);
      responseDiv.innerHTML = "❌ An unexpected error occurred.";
    } finally {
      // Always hide loading state
      this.hideLoading();
    }
  },

  /**
   * Handle accepted offer logic
   */
  async handleAcceptedOffer(offer, email, amountOff, productId, variantId, responseDiv) {
    try {
      console.log("📡 Submitting discount request:", {
        amount_off: amountOff.toFixed(2),
        product_id: productId,
        variant_id: variantId,
        email
      });

      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const res = await fetch(window.offerData.apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount_off: amountOff.toFixed(2),
          product_id: productId,
          variant_id: variantId,
          email: email
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();
      console.log("✅ API Response:", result);

      if (result.checkout_url) {
        // Validate checkout URL before displaying
        const urlValidation = OfferValidation.validateUrl(result.checkout_url);
        
        if (urlValidation.isValid) {
          responseDiv.innerHTML = `
            🎉 <strong>Offer accepted!</strong><br>
            <a href="${result.checkout_url}" class="btn" target="_blank" rel="noopener noreferrer" style="display:inline-block; margin-top:10px; border:1px solid #000; border-radius: 6px; padding: 8px 16px; text-decoration: none; color: #000;">
              Checkout for $${offer.toFixed(2)}
            </a>
          `;
        } else {
          console.error("❗ Invalid checkout URL:", urlValidation.error);
          responseDiv.innerHTML = "⚠️ Offer accepted, but we couldn't generate a valid checkout link.";
        }
      } else {
        responseDiv.innerHTML = "⚠️ Offer accepted, but we couldn't generate a checkout link.";
        console.error("❗ Missing checkout_url:", result);
      }
    } catch (err) {
      console.error("❌ Error sending offer:", err);
      if (err.name === 'AbortError') {
        responseDiv.innerHTML = "❌ Request timed out. Please try again.";
      } else {
        responseDiv.innerHTML = "❌ Something went wrong processing your offer.";
      }
    }
  },

  /**
   * Handle declined offer
   */
  handleDeclinedOffer(offer, responseDiv) {
    responseDiv.innerHTML = `
      ❌ Your offer of $${offer.toFixed(2)} was not accepted.<br>
      Try submitting a higher offer.
    `;
  },

  /**
   * Handle unexpected offer state
   */
  handleUnexpectedState(responseDiv) {
    responseDiv.innerHTML = `
      ❓ Unexpected offer state. Please try again.
    `;
  }
};

// Global functions for backward compatibility with onclick handlers
function closeOfferModal() {
  OfferModal.closeModal();
}

function submitOffer() {
  return OfferModal.submitOffer();
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => OfferModal.init());
} else {
  OfferModal.init();
}

// Make OfferModal available globally
if (typeof window !== 'undefined') {
  window.OfferModal = OfferModal;
}